import {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DataTable, type Column} from '@/components/data-table'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {NuevaEvaluacionDialog} from '@/components/bateria/nueva-evaluacion-dialog'
import {BateriaDescartarDialog} from '@/components/bateria/bateria-descartar-dialog'
import {
  BATERIA_SOLICITUDES_PAGE_SIZE,
  useBateriaSolicitudesAdmin,
  usePatchBateriaSolicitud,
  type BateriaSolicitudAdmin,
} from '@/hooks/bateria/use-bateria-solicitudes'
import {translateApiError} from '@/lib/errors'

const dateFormat = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})
const MS_PER_DAY = 86_400_000

const estadoTone: Record<BateriaSolicitudAdmin['estado'], 'amber' | 'green' | 'neutral'> = {
  pendiente: 'amber',
  atendida: 'green',
  entregada: 'neutral',
  descartada: 'neutral',
}

function antiguedad(t: (key: string, opts?: Record<string, unknown>) => string, createdAt: string): string {
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / MS_PER_DAY)
  if (days <= 0) return t('bateriaSolicitudes.admin.antiguedadHoy')
  if (days === 1) return t('bateriaSolicitudes.admin.antiguedadAyer')
  return t('bateriaSolicitudes.admin.antiguedadDias', {count: days})
}

type Variant = 'embedded' | 'standalone'

/** `embedded`: solo pendientes de la empresa abierta, oculta si no hay; `standalone`: la cola de toda la plataforma. */
export function BateriaSolicitudesSection({variant = 'embedded'}: {variant?: Variant} = {}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const {data} = useBateriaSolicitudesAdmin(page, true)
  const patch = usePatchBateriaSolicitud()
  const [descartando, setDescartando] = useState<BateriaSolicitudAdmin | null>(null)
  const [atendiendo, setAtendiendo] = useState<BateriaSolicitudAdmin | null>(null)
  const [linkError, setLinkError] = useState<string | null>(null)

  const items = data?.items ?? []
  const rows = variant === 'embedded' ? items.filter((s) => s.estado === 'pendiente') : items
  if (variant === 'embedded' && (!data || data.total === 0 || rows.length === 0)) return null

  const columns: Column<BateriaSolicitudAdmin>[] = [
    {
      key: 'empresa',
      // Sin ancho en móvil, `table-fixed` colapsa empresa y nota a cero a 390px; con él la tabla hace scroll.
      width: 'w-44 sm:w-auto',
      header: t('bateriaSolicitudes.admin.table.empresa'),
      cell: (row) => (
        <span className="block min-w-0">
          <span className="block truncate font-medium text-foreground">{row.business_name}</span>
          <span className="block truncate font-mono text-xs text-muted-foreground">{row.business_nit}</span>
        </span>
      ),
    },
    {
      key: 'fecha',
      header: t('bateriaSolicitudes.admin.table.fecha'),
      width: 'w-28',
      cell: (row) => (
        <span className="block font-mono text-xs text-muted-foreground">
          {dateFormat.format(new Date(row.created_at))}
        </span>
      ),
    },
    ...(variant === 'standalone'
      ? [
          {
            key: 'antiguedad',
            header: t('bateriaSolicitudes.admin.table.antiguedad'),
            width: 'w-28',
            cell: (row) => <span className="block text-xs text-muted-foreground">{antiguedad(t, row.created_at)}</span>,
          } satisfies Column<BateriaSolicitudAdmin>,
        ]
      : []),
    {
      key: 'solicitante',
      header: t('bateriaSolicitudes.admin.table.solicitante'),
      width: 'w-36',
      cell: (row) => <span className="block truncate text-xs text-muted-foreground">{row.requested_by_name}</span>,
    },
    {
      key: 'nota',
      width: 'w-48 sm:w-auto',
      header: t('bateriaSolicitudes.admin.table.nota'),
      cell: (row) => <span className="block truncate text-xs text-muted-foreground">{row.nota ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: variant === 'standalone' ? t('bateriaSolicitudes.admin.table.estado') : '',
      width: 'w-56',
      className: 'text-right',
      cell: (row) =>
        row.estado === 'pendiente' ? (
          <div className="flex flex-wrap items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" data-testid={`bateria-atender-${row.id}`} onClick={() => setAtendiendo(row)}>
              {t('bateriaSolicitudes.admin.crearEvaluacion')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              data-testid={`bateria-descartar-${row.id}`}
              onClick={() => setDescartando(row)}>
              {t('bateriaSolicitudes.admin.descartar')}
            </Button>
          </div>
        ) : variant === 'standalone' ? (
          <span className="flex justify-end">
            <Tag tone={estadoTone[row.estado]}>{t(`bateriaSolicitudes.admin.estado.${row.estado}`)}</Tag>
          </span>
        ) : null,
    },
  ]

  return (
    <>
      <ListCard
        testId={variant === 'standalone' ? 'bateria-solicitudes-queue' : 'bateria-solicitudes-section'}
        title={variant === 'embedded' ? t('bateriaSolicitudes.admin.title') : undefined}
        actions={
          variant === 'embedded' ? (
            <Tag tone="amber" data-testid="bateria-solicitudes-pendientes">
              {t('bateriaSolicitudes.admin.pendientesCount', {count: rows.length})}
            </Tag>
          ) : undefined
        }
        toolbar={linkError ? <p className="text-sm text-destructive">{linkError}</p> : undefined}
        footer={
          data &&
          data.total > BATERIA_SOLICITUDES_PAGE_SIZE && (
            <PaginationBar
              page={page}
              pageSize={BATERIA_SOLICITUDES_PAGE_SIZE}
              total={data.total}
              onPageChange={setPage}
            />
          )
        }>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          emptyState={
            variant === 'standalone' ? (
              <ListEmptyState page title={t('bateriaSolicitudes.admin.emptyTitle')} />
            ) : undefined
          }
        />
      </ListCard>

      <BateriaDescartarDialog target={descartando} onClose={() => setDescartando(null)} />
      {atendiendo && (
        <NuevaEvaluacionDialog
          businessId={atendiendo.business_id}
          open={!!atendiendo}
          onOpenChange={(open) => !open && setAtendiendo(null)}
          onCreated={(evaluacionId) => {
            const solicitud = atendiendo
            setLinkError(null)
            patch.mutate(
              {solicitudId: solicitud.id, action: 'atender', evaluacion_id: evaluacionId},
              {
                onSuccess: () =>
                  navigate(`/admin/bateria/empresas/${solicitud.business_id}/evaluaciones/${evaluacionId}`),
                // La evaluación ya existe: se avisa el fallo del vínculo y se navega igual; la solicitud sigue pendiente.
                onError: (err) => {
                  setLinkError(translateApiError(err))
                  navigate(`/admin/bateria/empresas/${solicitud.business_id}/evaluaciones/${evaluacionId}`)
                },
              }
            )
            setAtendiendo(null)
          }}
        />
      )}
    </>
  )
}
